import { useState, useEffect, useMemo } from 'react';
import { useQuery } from '@apollo/client';

import { KEYS, UseBillingRepositoryType, useInjection } from 'application/providers/DIContainerProvider';
import { TOP_BANNER_TYPE_CLOSED } from 'application/consts';
import { useAppSelector } from 'application/providers/StoreProvider';
import { selectShowNewPaymentSchemeBanner } from 'application/store';

import {
  AuthCurrentUserDocument,
  AuthCurrentUserQuery,
  Country,
  PaymentKind,
  SubscriptionStatus,
} from 'domain/api/graphql/generated';
import { FetchPolicyKeys } from 'domain/api/graphql/types';

import DateUtils from '@zvuk-b2b/react-uikit/toolkit/DateUtils';
import { MONTHS_GENITIVE } from '@zvuk-b2b/react-uikit/toolkit/constants';

export enum BANNER_TYPE {
  GRACE = 'GRACE',
  UNPAID = 'UNPAID',
  TRIAL = 'TRIAL',
  PAYMENT = 'PAYMENT',
}

export type TopBannerState = {
  type: BANNER_TYPE;
  trialDaysLeft?: number;
};

export const resetTopBannerTypeClosed = () => localStorage.removeItem(TOP_BANNER_TYPE_CLOSED);

function useTopBannerState() {
  const showNewPaymentSchemeBanner = useAppSelector(selectShowNewPaymentSchemeBanner);

  const { data } = useQuery<AuthCurrentUserQuery>(AuthCurrentUserDocument, {
    fetchPolicy: FetchPolicyKeys.CACHE_AND_NETWORK,
  });

  const useBillingRepository = useInjection<UseBillingRepositoryType>(KEYS.BILLING_REPOSITORY);

  const [hiddenTopBannerState, setHiddenTopBannerState] = useState(
    localStorage.getItem(TOP_BANNER_TYPE_CLOSED) as BANNER_TYPE | undefined
  );

  const currentUser = data?.currentUserPureQuery;
  const currentCompany = currentUser?.mainCompany;

  const { getBillingInfo, watchBillingInfo } = useBillingRepository();

  const billingInfo = watchBillingInfo(currentCompany?.id || '');

  const trialPeriod = currentCompany?.trialPeriod;

  const trialDaysLeft = useMemo(
    () => DateUtils.getDaysLeft(trialPeriod?.lengthMinutes, trialPeriod?.lengthDays, trialPeriod?.startedAt),
    [trialPeriod?.lengthMinutes, trialPeriod?.lengthDays, trialPeriod?.startedAt]
  );

  const isBillingEnabled = !!currentCompany?.isBillingEnabled;

  const startDate = new Date(billingInfo.nextBillingDate);

  const formatNextBillingDate = DateUtils.getFormatDateFrom(startDate, 'dd.MM.yyyy');
  const formatStartNewSchemeDate = '01.04.2026';
  const monthGenitive = MONTHS_GENITIVE[startDate.getMonth()];

  const isGraceBanner = isBillingEnabled && billingInfo.nextBillingDate && billingInfo.hasGraceLicenses;

  const isUnpaidBanner = isBillingEnabled && billingInfo.hasInactiveLicenses;

  const isTrialBannerKz =
    currentCompany?.country === Country.Kz &&
    currentCompany?.trialPeriod?.isFinished === false &&
    currentCompany?.trialPeriod?.isExpired === false;

  const isTrialBannerBy =
    currentCompany?.country === Country.By &&
    currentCompany?.trialPeriod?.isFinished === false &&
    currentCompany?.trialPeriod?.isExpired === false;

  const isTrialBanner = billingInfo.hasTrialLicenses;

  const isNewPaymentSchemeBanner =
    showNewPaymentSchemeBanner &&
    isBillingEnabled &&
    (billingInfo.status === SubscriptionStatus.TrialSubscription ||
      billingInfo.status === SubscriptionStatus.ActiveSubscription) &&
    startDate.getDate() !== 1 &&
    billingInfo.renewableLicenses?.length !== 0;

  useEffect(() => {
    if (currentUser?.id && !billingInfo.status) {
      getBillingInfo(currentCompany?.id!);
    }
  }, [currentUser?.id]);

  let banner: TopBannerState | undefined;

  if (currentCompany) {
    if (isTrialBanner || isTrialBannerKz || isTrialBannerBy) {
      banner = {
        type: BANNER_TYPE.TRIAL,
        trialDaysLeft,
      };
    }

    if (isGraceBanner) {
      banner = {
        type: BANNER_TYPE.GRACE,
      };
    }

    if (isUnpaidBanner) {
      banner = {
        type: BANNER_TYPE.UNPAID,
      };
    }

    if (isNewPaymentSchemeBanner) {
      banner = {
        type: BANNER_TYPE.PAYMENT,
      };
    }
  }

  return {
    banner,
    isVisible: !!banner && banner?.type !== hiddenTopBannerState,
    isSBBOLPayment: billingInfo?.selectedPaymentKind === PaymentKind.Sbbol,
    onClose: () => {
      setHiddenTopBannerState(banner?.type);
      if (banner?.type) {
        localStorage.setItem(TOP_BANNER_TYPE_CLOSED, banner?.type);
      } else {
        resetTopBannerTypeClosed();
      }
    },
    country: currentCompany?.country || Country.Ru,
    formatNextBillingDate,
    formatStartNewSchemeDate,
    monthGenitive,
  };
}

export default useTopBannerState;
